import { ElementType, type MouseEventHandler, type ReactNode, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Interpolation, Theme } from 'node_modules/@emotion/react/dist/declarations/src';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { getFullName } from '@frontend/name';
import { Photos } from '@frontend/photos';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useContactPanelShallowStore } from '@frontend/shared';
import { type GroupParticipant, type ThreadHeaderSize } from '@frontend/thread-header-context';
import { useDebouncedValue } from '@frontend/timer';
import { theme } from '@frontend/theme';
import {
  Avatar,
  CircularAvatarGroup,
  Heading,
  HeadingProps,
  PolymorphicComponentPropWithoutRef,
  TextProps,
  Text,
  AvatarProps,
  NakedButton,
  useTooltip,
} from '@frontend/design-system';
import { BlockedIndicatorInline } from './blocked-indicator-inline';
import { GroupContactsDrawer } from './group-contacts-drawer';
import { GroupThreadTitle } from './group-thread-title';
import { useGroupBlockedStatus } from './hooks/use-group-blocked-status';
import { useGroupOptOutStatus } from './hooks/use-group-opt-out-status';
import { OptOutIndicatorInline } from './opt-out-indicator-inline';

type CustomTextProps<E extends 'heading' | 'text'> = {
  type: E;
} & (E extends 'text' ? TextProps : HeadingProps);

export type ThreadTitleProps<
  E extends ElementType = 'div',
  TextType extends 'heading' | 'text' = 'heading'
> = PolymorphicComponentPropWithoutRef<
  E,
  {
    text: string;
    subtext?: string;
    subtextSize?: TextProps['size'];
    personId?: string;
    secondaryPerson?: {
      personId: string;
      firstName: string;
      preferredName: string;
      lastName: string;
    };
    groupId?: string;
    variant?: ThreadHeaderSize;
    textProps?: CustomTextProps<TextType>;
    avatarProps?: AvatarProps;
    groupAvatars?: AvatarProps[];
    openContactPanel?: boolean;
    onBackClick?: () => void;
    onSecondaryPersonClick?: () => void;
    isCompactMode?: boolean;
    headOfHouseholdId?: string;
    nameTooltipContent?: string;
    participants?: GroupParticipant[];
    groupContactsDrawerRowSlot?: (personId: string) => ReactNode;
    onGroupIconClick?: () => void;
    GroupParticipantClick?: (personId: string) => void;
    setIsChangingContextContact?: (isChanging: boolean) => void;
    isChangingContextContact?: boolean;
    groupMessagingEnabled?: boolean;
    groupName?: string;
    onGroupNameSave?: (name?: string) => void;
  }
>;

const titleTextBaseStyles = {
  lineHeight: 1,
  overflow: 'hidden' as const,
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
};

// The `css` prop is injected by Emotion's JSX pragma and isn't part of TextProps/HeadingProps,
// so we explicitly include it to allow constructing these prop objects outside of JSX.
type EmotionCssProp = { css?: Interpolation<Theme> };

type TitleTextWithTooltipProps = {
  children: ReactNode;
  onTitleClick: MouseEventHandler<HTMLElement>;
  nameTooltipContent?: string;
} & (
  | { component: 'text'; textComponentProps: TextProps & EmotionCssProp }
  | { component: 'heading'; textComponentProps: HeadingProps & EmotionCssProp }
);

/**
 * Renders a title text element (`Text` or `Heading`) wrapped in an optional name tooltip.
 * Consolidates the repeated tooltip-trigger-wrapper + conditional-tooltip pattern used
 * across compact-mode, text-type, and heading-type title rendering in `ThreadTitle`.
 */
const TitleTextWithTooltip = ({
  component,
  textComponentProps,
  onTitleClick,
  nameTooltipContent,
  children,
}: TitleTextWithTooltipProps) => {
  const {
    Tooltip: NameTooltip,
    triggerProps: nameTooltipTriggerProps,
    tooltipProps: nameTooltipProps,
  } = useTooltip({ placement: 'top', hoverDelay: 300 });

  return (
    <>
      <div
        css={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        {...(nameTooltipContent ? nameTooltipTriggerProps : {})}
      >
        {component === 'text' ? (
          <Text {...(textComponentProps as TextProps)} onClick={onTitleClick}>
            {children}
          </Text>
        ) : (
          <Heading level={2} {...(textComponentProps as HeadingProps)} onClick={onTitleClick}>
            {children}
          </Heading>
        )}
      </div>
      {nameTooltipContent && <NameTooltip {...nameTooltipProps}>{nameTooltipContent}</NameTooltip>}
    </>
  );
};

/**
 * `ThreadTitle` is a component that displays the title of the thread. This includes the `Avatar`, and title text (usually
 * the person name or phone number)
 * Its children will be rendered inside the title, after the avatar and title text. This is a useful place to put the
 * `PersonSecondaryInfo` and `AssociatedContactsButton` components.
 *
 * If the `variant` is 'vertical', its children will be displayed below the title text.
 *
 * @param text - The title text to display.
 * @param subtext (optional) - If provided, it will be displayed below the title text, and the children will always be rendered to the right of the title text.
 * @param personId (optional) - The ID of the person associated with the thread. If provided, the title text will be clickable and
 * will open the contact panel for the person.
 * @param secondaryPerson (optional) - The data of the secondary person whose avater will be displayed. Should normally
 * be used as the `primaryContact`/`Phone Owner` when the provided `personId` is different.
 * @param groupId (optional) - The ID of the group to which the person belongs.
 * @param variant (optional) - The layout of the title. Can be 'horizontal' or 'vertical'. Defaults to 'horizontal'.
 * @param textProps (optional) - The props to pass to the title text component. If the `type` is 'text', a `Text` component will be
 * used. If the `type` is 'heading', a `Heading` component will be used. Defaults to a `Heading` component.
 * @param avatarProps (optional) - The props to pass to the `Avatar` component.
 * @param openContactPanel (optional) - Whether the contact panel should be opened when the title text is clicked. Defaults to `true`.
 * @param onBackClick (optional) - A callback function that will be called when the back button is clicked. If provided, a back button
 * will be displayed before the avatar.
 * @param onSecondaryPersonClick (optional) - A callback function that will be called when the secondary person's avatar is clicked.
 * Useful to clear the context contact or align the context with the secondary person/phone owner.
 * @param headOfHouseholdId (optional) - The ID of the head of household. Used to display a badge on the avatar if the `personId` matches the `headofhouseholdId`.
 */
export const ThreadTitle = <E extends ElementType = 'div', TextType extends 'heading' | 'text' = 'heading'>({
  as,
  text,
  subtext,
  subtextSize = 'small',
  textProps,
  avatarProps,
  groupAvatars,
  personId,
  secondaryPerson,
  groupId,
  openContactPanel = true,
  onBackClick,
  onSecondaryPersonClick,
  isCompactMode,
  headOfHouseholdId,
  nameTooltipContent,
  participants,
  groupContactsDrawerRowSlot,
  onGroupIconClick,
  GroupParticipantClick,
  setIsChangingContextContact,
  isChangingContextContact,
  groupMessagingEnabled = false,
  groupName,
  onGroupNameSave,
  children,
  variant = 'normal',
  ...rest
}: ThreadTitleProps<E, TextType>) => {
  const Component = as || 'div';
  const { t } = useTranslation('thread-header');

  const [hidePrimaryLabel, setHidePrimaryLabel] = useState<boolean>(false);
  const [localGroupName, setLocalGroupName] = useState<string | undefined>(undefined);

  const { setPersonId } = useContactPanelShallowStore('setPersonId');

  const isSmallMode = useMatchMedia({ maxWidth: breakpoints.xsmall.max });

  const showSecondaryAvatar = !!secondaryPerson?.personId && secondaryPerson?.personId !== personId;
  const showGroupThreadTitle = groupMessagingEnabled && !!participants?.length && participants.length > 1;
  const [groupContactsDrawerOpen, setGroupContactsDrawerOpen] = useState(false);
  const { debouncedValue: debouncedShowSecondaryAvatar } = useDebouncedValue(showSecondaryAvatar, 300);
  const avatarSize = avatarProps?.size ?? (isSmallMode || variant === 'small' ? 'small' : 'large');

  const {
    Tooltip,
    triggerProps: tooltipTriggerProps,
    tooltipProps,
  } = useTooltip({ placement: 'bottom', hoverDelay: 300 });
  const shouldShowGroupAvatar = groupMessagingEnabled && !!groupAvatars?.length;
  const resolvedGroupName = groupName ?? localGroupName;

  // Group opt-out and blocked detection for the header indicators
  const { hasOptedOutParticipants, optedOutParticipants } = useGroupOptOutStatus({
    participants: participants ?? [],
    locationId: groupId ?? '',
    enabled: showGroupThreadTitle && !!groupId,
  });

  const { blockedParticipants } = useGroupBlockedStatus({
    participants: participants ?? [],
    locationId: groupId ?? '',
    enabled: showGroupThreadTitle && !!groupId,
  });

  // Only show blocked indicator if there are blocked-only participants (not also opted-out)
  const optedOutPhoneSet = useMemo(
    () => new Set(optedOutParticipants.map((p) => p.phoneNumber).filter(Boolean)),
    [optedOutParticipants]
  );
  const hasBlockedOnlyParticipants = blockedParticipants.some(
    (p) => p.phoneNumber && !optedOutPhoneSet.has(p.phoneNumber)
  );

  const handleGroupNameSave = (nextName?: string) => {
    if (onGroupNameSave) {
      onGroupNameSave(nextName);
      return;
    }

    setLocalGroupName(nextName);
  };

  const handleTitleClick: MouseEventHandler<HTMLElement> = (e) => {
    if (personId && openContactPanel) {
      setPersonId(personId, true, groupId);
    }
    textProps?.onClick?.(e);
  };

  const handleGroupIconButtonClick = () => {
    if (onGroupIconClick) {
      onGroupIconClick();
      return;
    }

    setGroupContactsDrawerOpen(true);
  };

  return (
    <Component
      css={[
        {
          display: 'flex',
          flexDirection: 'row',
          justifyContent: variant === 'small' ? 'center' : 'start',
          alignItems: 'center',
          columnGap: theme.spacing(1),
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
        },
      ]}
      {...rest}
    >
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          columnGap: variant === 'small' ? theme.spacing(0.5) : theme.spacing(1),
        }}
      >
        <AnimatePresence>
          <>
            {!!showSecondaryAvatar && (
              <motion.div
                key='secondary-avatar'
                css={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  marginRight: theme.spacing(-0.5),
                  cursor: 'pointer',
                  overflow: 'hidden',
                }}
                initial={{ width: 0, opacity: 0 }}
                animate={debouncedShowSecondaryAvatar ? { width: 'auto', opacity: 1 } : { width: 0, opacity: 0 }}
                exit={{ width: 0, opacity: 0 }}
                onMouseOver={(e) => {
                  e.stopPropagation();
                  setHidePrimaryLabel(false);
                }}
              >
                <Photos.ContactProfilePhoto
                  personId={secondaryPerson.personId}
                  locationId={groupId}
                  name={getFullName(secondaryPerson)}
                  firstName={secondaryPerson.preferredName || secondaryPerson.firstName}
                  lastName={secondaryPerson.lastName}
                  size={variant === 'small' ? 'xs' : 'small'}
                  disableHoverLabel={hidePrimaryLabel}
                >
                  {headOfHouseholdId === secondaryPerson.personId && (
                    <span
                      css={[householdBadgeStyles, householdBadgeSmallStyles]}
                      onMouseOver={(e) => {
                        e.stopPropagation();
                        setHidePrimaryLabel(true);
                      }}
                      {...tooltipTriggerProps}
                    >
                      <Icon name='crown-small' size={10} />
                      <Tooltip {...tooltipProps}>{t('Head of Household')}</Tooltip>
                    </span>
                  )}
                </Photos.ContactProfilePhoto>
                <NakedButton
                  onClick={onSecondaryPersonClick}
                  css={{ padding: theme.spacing(0.5, 0, 0, 2), width: '100%' }}
                >
                  <div
                    data-testid='return-focus-to-primary-contact'
                    css={{
                      width: 16,
                      height: 8,
                      borderLeft: `1px solid ${theme.colors.neutral20}`,
                      borderBottom: `1px solid ${theme.colors.neutral20}`,
                      borderBottomLeftRadius: 6,
                    }}
                  />
                </NakedButton>
              </motion.div>
            )}
            {shouldShowGroupAvatar ? (
              <CircularAvatarGroup avatars={groupAvatars} size={avatarSize} />
            ) : personId ? (
              <div
                onMouseOver={(e) => {
                  e.stopPropagation();
                  setHidePrimaryLabel(false);
                }}
              >
                <Photos.ContactProfilePhoto
                  personId={personId}
                  locationId={groupId}
                  name={text}
                  disableClick={!openContactPanel}
                  {...avatarProps}
                  title={
                    showSecondaryAvatar
                      ? t('{{phoneOwnerName}} (Phone Owner)\n{{contextContactName}}', {
                          phoneOwnerName: getFullName(secondaryPerson),
                          contextContactName: text,
                        })
                      : avatarProps?.title
                  }
                  size={avatarSize}
                  disableHoverLabel={hidePrimaryLabel}
                >
                  {headOfHouseholdId === personId && (
                    <span
                      css={householdBadgeStyles}
                      onMouseOver={(e) => {
                        e.stopPropagation();
                        setHidePrimaryLabel(true);
                      }}
                      {...tooltipTriggerProps}
                    >
                      <Icon name='crown' />
                      <Tooltip {...tooltipProps}>{t('Head of Household')}</Tooltip>
                    </span>
                  )}
                </Photos.ContactProfilePhoto>
              </div>
            ) : (
              <Avatar name={personId ? text : undefined} {...avatarProps} size={avatarSize} />
            )}
          </>
        </AnimatePresence>
        {isCompactMode && !showGroupThreadTitle && (
          <TitleTextWithTooltip
            component='text'
            textComponentProps={{
              as: 'span',
              css: { cursor: 'pointer', ...titleTextBaseStyles },
              ...(textProps as TextProps),
              textAlign: variant === 'small' ? 'center' : undefined,
            }}
            onTitleClick={handleTitleClick}
            nameTooltipContent={nameTooltipContent}
          >
            <Text as='span' css={{ fontWeight: theme.font.weight.bold, textTransform: 'capitalize' }}>
              {avatarProps?.firstName || text}
            </Text>
            {` ${avatarProps?.lastName || ''}`}
          </TitleTextWithTooltip>
        )}
      </div>
      <div
        css={{
          display: 'flex',
          justifyContent: 'start',
          columnGap: theme.spacing(0.5),
          rowGap: theme.spacing(0.25),
          minWidth: 0,
          flex: 1,
          flexDirection: 'row',
          flexWrap: isSmallMode ? 'nowrap' : 'wrap',
          alignItems: 'center',
        }}
      >
        <div
          css={[
            {
              minWidth: 0,
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: showGroupThreadTitle ? '100%' : '',
            },
            !!subtext && {
              display: 'flex',
              flexDirection: 'column',
            },
          ]}
        >
          {showGroupThreadTitle ? (
            <GroupThreadTitle
              participants={participants!}
              isCompactMode={isCompactMode}
              isBold
              onGroupIconClick={handleGroupIconButtonClick}
              showContactsPaneButton={true}
              isEditable
              groupName={resolvedGroupName}
              onGroupNameSave={handleGroupNameSave}
              titleMaxWidth={isSmallMode ? 'calc(100% - 100px)' : '60%'}
              optOutIndicator={
                hasOptedOutParticipants || hasBlockedOnlyParticipants ? (
                  <>
                    {hasOptedOutParticipants && (
                      <OptOutIndicatorInline
                        show={true}
                        tooltipLabel={t('One or more people in this group have opted out.')}
                      />
                    )}
                    {hasBlockedOnlyParticipants && (
                      <BlockedIndicatorInline
                        show={true}
                        tooltipLabel={t('One or more people in this group is blocked.')}
                      />
                    )}
                  </>
                ) : undefined
              }
            />
          ) : (
            !isCompactMode &&
            (textProps?.type === 'text' ? (
              <TitleTextWithTooltip
                component='text'
                textComponentProps={{
                  css: [{ cursor: 'pointer', ...titleTextBaseStyles, maxWidth: '100%' }],
                  ...(textProps as TextProps),
                }}
                onTitleClick={handleTitleClick}
                nameTooltipContent={nameTooltipContent}
              >
                {text}
              </TitleTextWithTooltip>
            ) : (
              <TitleTextWithTooltip
                component='heading'
                textComponentProps={{
                  css: [
                    {
                      cursor: openContactPanel ? 'pointer' : 'default',
                      fontSize: 'var(--fabric-font-size-md)',
                      ...titleTextBaseStyles,
                      maxWidth: '100%',
                      fontWeight: 'var(--fabric-font-weight-bold)',
                    },
                  ],
                  ...(textProps as HeadingProps),
                }}
                onTitleClick={handleTitleClick}
                nameTooltipContent={nameTooltipContent}
              >
                {avatarProps?.firstName || text}
                {avatarProps?.firstName && avatarProps?.lastName && ' '}
                <span css={{ fontWeight: 'var(--fabric-font-weight-regular)', textTransform: 'capitalize' }}>
                  {avatarProps?.lastName || ''}
                </span>
              </TitleTextWithTooltip>
            ))
          )}
          {!!subtext && (
            <Text color='subdued' size={subtextSize} textAlign='left'>
              {subtext}
            </Text>
          )}
        </div>
        {children && <div css={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{children}</div>}
      </div>
      {showGroupThreadTitle && (
        <GroupContactsDrawer
          open={isChangingContextContact || groupContactsDrawerOpen}
          onClose={() => {
            setGroupContactsDrawerOpen(false);
            setIsChangingContextContact?.(false);
          }}
          participants={participants}
          locationId={groupId}
          renderRowRightSlot={groupContactsDrawerRowSlot}
          onSelect={(personId) => {
            GroupParticipantClick?.(personId);
          }}
        />
      )}
    </Component>
  );
};

const householdBadgeStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'absolute' as const,
  top: '-3px',
  right: '-7px',
  height: '20px',
  width: '20px',
  backgroundColor: theme.colors.warning10,
  borderRadius: theme.borderRadius.full,
  boxShadow: theme.shadows.light,
  color: theme.colors.warning60,
};

const householdBadgeSmallStyles = {
  top: 0,
  right: 0,
  height: '10px',
  width: '10px',
};
